// TFVF Manager Training Dashboard — site-scoped roll-up of team
// completion, acknowledgements and quiz scores, plus a "Assign training"
// dialog.
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Users, Download, Send, CheckCircle2, Circle, Clock } from 'lucide-react';
import { API, authHeaders, pct } from './tfvfShared';
import { useAuth } from '../../../contexts/AuthContext';

const TfvfManager = () => {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ user_ids: [], module_id: '', due_date: '', note: '' });

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/tfvf/manager/dashboard`, { headers: authHeaders(token) })
      .then(r => setData(r.data))
      .catch(err => toast.error(err?.response?.data?.detail || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const download = async () => {
    const r = await fetch(`${API}/tfvf/reports/completion.csv`, { headers: authHeaders(token) });
    if (!r.ok) { toast.error('Export failed'); return; }
    const b = await r.blob();
    const u = URL.createObjectURL(b);
    const a = document.createElement('a');
    a.href = u; a.download = 'tfvf_completion.csv'; a.click();
    URL.revokeObjectURL(u);
    toast.success('Downloaded completion CSV');
  };

  const submitAssign = async () => {
    if (!assignForm.user_ids.length || !assignForm.module_id) {
      toast.info('Pick at least one staff member and a module'); return;
    }
    try {
      await axios.post(`${API}/tfvf/manager/assign`, assignForm, { headers: authHeaders(token) });
      toast.success('Training assigned');
      setAssignOpen(false);
      setAssignForm({ user_ids: [], module_id: '', due_date: '', note: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Assign failed');
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--tf-forest)]/50" /></div>;
  if (!data) return null;

  const modules = Object.entries(data.learning_paths || {})[0]?.[1] || [];

  return (
    <div className="space-y-5" data-testid="tfvf-manager-page">
      <div className="tf-card rounded-xl p-5 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="tf-eyebrow flex items-center gap-2"><Users className="w-3 h-3" /> Team training dashboard</p>
          <h2 className="text-xl font-bold text-[color:var(--tf-forest)] mt-1">Site-scoped Completion & Progress</h2>
          <p className="text-xs text-[color:var(--tf-muted)] mt-1">
            {user?.role === 'domain_supervisor' ? 'Showing only your allocated sites.' : 'Showing your entire tenant.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={download} className="tf-btn-primary rounded-lg px-3 py-2 text-sm font-semibold inline-flex items-center gap-1" data-testid="tfvf-mgr-export">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={() => setAssignOpen(true)} className="tf-btn-gold rounded-lg px-3 py-2 text-sm font-semibold inline-flex items-center gap-1" data-testid="tfvf-mgr-assign-btn">
            <Send className="w-4 h-4" /> Assign training
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          ['Assigned',   data.tiles.assigned],
          ['Certified',  data.tiles.completed_all_required],
          ['In progress',data.tiles.in_progress],
          ['Not started',data.tiles.not_started],
          ['Avg quiz',   `${data.tiles.avg_quiz_score}%`],
          ['Completion', pct(data.tiles.completion_pct)],
        ].map(([label, val]) => (
          <div key={label} className="tf-card rounded-lg p-3 text-center" data-testid={`tfvf-mgr-tile-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <p className="tf-eyebrow">{label}</p>
            <p className="text-xl font-bold text-[color:var(--tf-forest)] mt-1">{val}</p>
          </div>
        ))}
      </div>

      {/* Staff table */}
      <div className="tf-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[color:var(--tf-parchment)] text-[color:var(--tf-forest)]">
              <tr>
                <th className="text-left px-4 py-2 sticky left-0 bg-[color:var(--tf-parchment)]">Staff</th>
                <th className="text-left px-2 py-2">Path</th>
                <th className="text-left px-2 py-2">Site</th>
                <th className="text-center px-2 py-2">Ack</th>
                {modules.map(mid => (
                  <th key={mid} className="text-center px-2 py-2 text-[10px] font-bold whitespace-nowrap">{mid.replace('TFVF-','')}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan={4 + modules.length} className="text-center py-6 text-[color:var(--tf-muted)] italic">No staff visible in scope yet.</td></tr>
              ) : data.rows.map(row => (
                <tr key={row.user_id} className="border-t border-[color:var(--tf-forest)]/5 hover:bg-[color:var(--tf-parchment)]/40" data-testid={`tfvf-mgr-row-${row.user_id}`}>
                  <td className="px-4 py-2 sticky left-0 bg-white">
                    <p className="font-semibold text-[color:var(--tf-forest)]">{row.name || row.email}</p>
                    <p className="text-[10px] text-[color:var(--tf-muted)]">{row.role}</p>
                  </td>
                  <td className="px-2 py-2 text-xs">{row.training_role}</td>
                  <td className="px-2 py-2 text-xs">{row.site || '—'}</td>
                  <td className="px-2 py-2 text-center">
                    {row.acknowledgement_signed_at
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" title={row.acknowledgement_signed_at} />
                      : <Circle className="w-4 h-4 text-[color:var(--tf-forest)]/20 mx-auto" />}
                  </td>
                  {modules.map(mid => {
                    const p = row.modules[mid];
                    if (!p) return <td key={mid} className="px-2 py-2 text-center"><Circle className="w-4 h-4 text-[color:var(--tf-forest)]/15 mx-auto" /></td>;
                    if (p.completed_at) return <td key={mid} className="px-2 py-2 text-center"><CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" title={`${p.quiz_score || ''}%`} /></td>;
                    if (p.viewed_at) return <td key={mid} className="px-2 py-2 text-center"><Clock className="w-4 h-4 text-amber-500 mx-auto" /></td>;
                    return <td key={mid} className="px-2 py-2 text-center"><Circle className="w-4 h-4 text-[color:var(--tf-forest)]/15 mx-auto" /></td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign dialog */}
      {assignOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setAssignOpen(false)}>
          <div className="tf-card rounded-xl p-6 max-w-lg w-full space-y-3" onClick={e => e.stopPropagation()} data-testid="tfvf-mgr-assign-dialog">
            <p className="tf-eyebrow">Assign training</p>
            <h3 className="text-lg font-bold text-[color:var(--tf-forest)]">Send a module to your team</h3>

            <div>
              <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Staff</label>
              <div className="mt-1 max-h-40 overflow-y-auto border border-[color:var(--tf-forest)]/15 rounded-lg p-2 space-y-1 bg-white">
                {data.rows.map(r => (
                  <label key={r.user_id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={assignForm.user_ids.includes(r.user_id)}
                      onChange={e => setAssignForm(f => ({
                        ...f,
                        user_ids: e.target.checked ? [...f.user_ids, r.user_id] : f.user_ids.filter(x => x !== r.user_id),
                      }))}
                    />
                    <span>{r.name || r.email}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Module</label>
              <select
                value={assignForm.module_id}
                onChange={e => setAssignForm(f => ({ ...f, module_id: e.target.value }))}
                className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white"
                data-testid="tfvf-mgr-assign-module"
              >
                <option value="">Pick a module</option>
                {modules.map(mid => <option key={mid} value={mid}>{mid}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Due date</label>
                <input
                  type="date"
                  value={assignForm.due_date}
                  onChange={e => setAssignForm(f => ({ ...f, due_date: e.target.value }))}
                  className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Focus criterion (optional)</label>
                <input
                  value={assignForm.focus_criterion || ''}
                  onChange={e => setAssignForm(f => ({ ...f, focus_criterion: e.target.value }))}
                  placeholder="e.g. green_brain"
                  className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-[color:var(--tf-forest)]">Note</label>
              <textarea
                value={assignForm.note}
                onChange={e => setAssignForm(f => ({ ...f, note: e.target.value }))}
                rows={2}
                className="w-full mt-1 border border-[color:var(--tf-forest)]/15 rounded-lg px-3 py-2 text-sm bg-white"
                placeholder="Why you're assigning this…"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => setAssignOpen(false)} className="px-3 py-2 text-sm text-[color:var(--tf-muted)] hover:text-[color:var(--tf-forest)]">Cancel</button>
              <button onClick={submitAssign} className="tf-btn-primary rounded-lg px-4 py-2 text-sm font-semibold" data-testid="tfvf-mgr-assign-submit">Assign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TfvfManager;
